/**
 * Cross-platform haptic feedback utility
 *
 * - Android: Uses Vibration API (navigator.vibrate)
 * - iOS: Limited support - uses AudioContext for tactile feedback simulation
 * - Desktop: Silent fallback
 */

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection'

// Detect platform safely
let isIOS = false
let isAndroid = false
let supportsVibration = false

try {
  isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
  isAndroid = typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent)
  supportsVibration = typeof navigator !== 'undefined' && 'vibrate' in navigator
} catch (e) {
  console.warn('[haptics] Error detecting platform:', e)
}

// Vibration patterns for different feedback types (in milliseconds)
const VIBRATION_PATTERNS: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 30],
  warning: [30, 50, 30],
  error: [50, 100, 50, 100, 50],
  selection: 5,
}

// Audio context for iOS haptic simulation (plays silent audio that triggers haptic engine)
let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext

  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    return audioContext
  } catch {
    return null
  }
}

/**
 * Trigger iOS-style haptic using AudioContext
 * This works because iOS triggers haptic feedback when audio plays
 */
function triggerIOSHaptic(style: HapticStyle): void {
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    // Create a very short, inaudible oscillator to trigger haptic
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Set frequency based on haptic style (lower = heavier feel)
    const frequencies: Record<HapticStyle, number> = {
      light: 200,
      medium: 150,
      heavy: 100,
      success: 180,
      warning: 120,
      error: 80,
      selection: 220,
    }

    oscillator.frequency.value = frequencies[style]
    oscillator.type = 'sine'

    // Very short duration, very low volume (inaudible but triggers haptic)
    gainNode.gain.value = 0.001

    const duration = style === 'heavy' || style === 'error' ? 0.05 : 0.02

    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + duration)
  } catch {
    // Silently fail if haptic not available
  }
}

/**
 * Trigger haptic feedback
 * @param style - Type of haptic feedback
 */
export function haptic(style: HapticStyle = 'light'): void {
  // Wrap everything in try-catch to never crash the app
  try {
    // Skip on desktop or if vibration not supported and not iOS
    if (!supportsVibration && !isIOS) return

    if (isIOS) {
      // iOS doesn't support Vibration API, use AudioContext workaround
      triggerIOSHaptic(style)
    } else if (supportsVibration) {
      // Android and other platforms with Vibration API
      const pattern = VIBRATION_PATTERNS[style]
      navigator.vibrate(pattern)
    }
  } catch (e) {
    // Silently ignore any haptic errors - never crash the app
    console.warn('[haptics] Error triggering haptic:', e)
  }
}

/**
 * Cancel any ongoing vibration
 */
export function cancelHaptic(): void {
  if (supportsVibration) {
    navigator.vibrate(0)
  }
}

/**
 * Check if haptic feedback is available on this device
 */
export function isHapticAvailable(): boolean {
  return supportsVibration || isIOS
}

/**
 * React hook-friendly wrapper for haptic feedback
 * Returns a function that triggers haptic on call
 */
export function createHapticHandler(style: HapticStyle = 'light') {
  return () => haptic(style)
}

/**
 * Wrap an onClick handler to include haptic feedback
 */
export function withHaptic<T extends (...args: any[]) => any>(
  handler: T | undefined,
  style: HapticStyle = 'light'
): T {
  return ((...args: Parameters<T>) => {
    haptic(style)
    return handler?.(...args)
  }) as T
}

// Export platform detection for conditional logic
export { isIOS, isAndroid, supportsVibration }
